import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { useTheme } from "@mui/material";

import indexOf from "lodash/indexOf";
import isNil from "lodash/isNil";
import merge from "lodash/merge";

/**
 * ToggleRouterTabSwitcher
 * - Visually identical to ToggleTabSwitcher
 * - Uses react-router navigation instead of (or in addition to) onChange
 *
 * Tabs can be:
 * 1) string[]  -> uses the same behavior as ToggleTabSwitcher (no router nav unless you supply getTo)
 * 2) { label: string, to: string, includes?: string[] }[]
 *
 * If `tabs` are objects with `to`, clicking navigates to that route.
 * Selected tab is derived from:
 *  - `value` if provided (label or index)
 *  - otherwise from current location matching `to` (startsWith).
 */

/**
   <ToggleRouterTabSwitcher
          tabs={[
            {
              label: "Loan Terms Sheet",
              to: `/loan/${loanId}/fees-and-closing/loan-terms`,
            },
            {
              label: "Wire Instructions",
              to: `/loan/${loanId}/fees-and-closing/wire-instructions`,
            },
          ]}
        />

         // String tabs + getTo mapper  
        <ToggleRouterTabSwitcher
          tabs={["Loan Terms Sheet", "Wire Instructions"]}
          getTo={(label) => {
            if (label === "Loan Terms Sheet")
              return `/loan/${loanId}/fees-and-closing/loan-terms`;
            if (label === "Wire Instructions")
              return `/loan/${loanId}/fees-and-closing/wire-instructions`;
            return null;
          }}
        />
 */
const ToggleRouterTabSwitcher = ({
  value,
  tabs,
  onChange,
  sx,
  variant, // "background" || "underline"
  useIndex = false,
  orientation = "horizontal",
  underlinePosition = "bottom",
  // optional: if tabs are strings, you can map a tab value to a route
  getTo, // (tabLabel, index) => string
  replace = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const normalizedTabs = useMemo(() => {
    return (tabs || []).map((t) => {
      if (typeof t === "string") {
        return { label: t, to: getTo ? getTo(t) : null };
      }
      return {
        label: t?.label,
        to: t?.to ?? null,
        includes: t?.includes,
      };
    });
  }, [tabs, getTo]);

  const deriveIndexFromLocation = useCallback(() => {
    const pathname = location.pathname || "";
    // pick the longest matching `to` so nested routes still highlight the correct tab
    let bestIdx = -1;
    let bestLen = -1;

    normalizedTabs.forEach((t, i) => {
      if (!t?.to) return;
      if (pathname.startsWith(t.to) && t.to.length > bestLen) {
        bestIdx = i;
        bestLen = t.to.length;
      }
    });

    return bestIdx;
  }, [location.pathname, normalizedTabs]);

  const selectedIndex = useMemo(() => {
    if (useIndex && !isNil(value)) return value;

    if (!useIndex && !isNil(value)) {
      // value is label
      const labels = normalizedTabs.map((t) => t.label);
      return indexOf(labels, value);
    }

    // fallback to router location
    return deriveIndexFromLocation();
  }, [useIndex, value, normalizedTabs, deriveIndexFromLocation]);

  const handleOnTabSwitch = useCallback(
    (event, newValue) => {
      if (isNil(newValue)) return;

      const tabObj = normalizedTabs[newValue];
      const nextLabel = tabObj?.label;

      // fire onChange to mirror old component behavior
      if (onChange) {
        if (useIndex) onChange(newValue);
        else onChange(nextLabel);
      }

      // router navigation if possible
      const to = tabObj?.to;
      if (to) {
        navigate(to, { replace });
      }
    },
    [normalizedTabs, onChange, useIndex, navigate, replace]
  );

  return (
    <TabSwitcherGroup
      orientation={orientation}
      variant={variant}
      underlinePosition={underlinePosition}
      value={selectedIndex}
      exclusive
      sx={sx}
      onChange={handleOnTabSwitch}
      aria-label="text alignment"
    >
      {normalizedTabs.map((tab, ind) => {
        return (
          <ToggleButton key={`${tab.label}-${ind}`} value={ind}>
            {tab.label}
          </ToggleButton>
        );
      })}
    </TabSwitcherGroup>
  );
};

const TabSwitcherGroup = ({
  value,
  children,
  full,
  onChange,
  orientation = "horizontal",
  ariaLabel,
  variant = "background",
  underlinePosition = "bottom",
  sx,
}) => {
  const theme = useTheme();

  const styles = useMemo(() => {
    const baseStyles = {
      flexGrow: full ? 1 : 0,
      ".MuiButtonBase-root": {
        flexGrow: full ? 1 : 0,
        color: "#181d1f",
        fontWeight: 400,
        lineHeight: 1.2,
        fontSize: "13px",
        ...(orientation === "vertical" && {
          whiteSpace: "nowrap",
          minWidth: "fit-content",
          justifyContent: "flex-start",
          borderLeft: "2px solid transparent",
        }),
        "&.Mui-selected": {
          color: "#2962ff",
          fontWeight: 700,
          ...(variant === "underline"
            ? {
                background: "none",
                borderRadius: "0 !important",
                fontWeight: 500,
              }
            : {
                background: "#2962ff14",
              }),
          ...(variant === "underline" && {
            borderTop: "none",
            borderRight: "none",
            borderBottom: "none",
            borderLeft: "none",
            ...(orientation === "horizontal" &&
              underlinePosition === "bottom" && {
                borderBottom: "2px solid #2962ff",
              }),
            ...(orientation === "horizontal" &&
              underlinePosition === "top" && {
                borderTop: "2px solid #2962ff",
              }),
            ...(orientation === "vertical" &&
              underlinePosition === "left" && {
                borderLeft: "2px solid #2962ff",
              }),
            ...(orientation === "vertical" &&
              underlinePosition === "right" && {
                borderRight: "2px solid #2962ff",
              }),
          }),
        },
      },

      "& .MuiToggleButtonGroup-grouped": {
        margin: theme?.spacing(0.2),
        border: 0,
        borderLeft: "2px solid transparent",
        textTransform: "capitalize",
        "&.Mui-disabled": {
          border: 0,
        },
        "&:not(:first-of-type)": {
          borderRadius: "4px",
        },
        "&:first-of-type": {
          borderRadius: "4px",
        },
      },
    };

    const variants = {
      underline: {
        ".MuiButtonBase-root": {
          fontSize: "11px",
        },
      },
    };

    const variantStyles = variants[variant] ?? {};
    const mergedStyles = merge(merge({}, baseStyles), variantStyles);
    const finalStyles = merge(mergedStyles, sx);
    return finalStyles;
  }, [sx, variant, full, theme, orientation, underlinePosition]);

  return (
    <ToggleButtonGroup
      size="small"
      className="toggle-tab-switcher-root"
      orientation={orientation}
      value={value}
      exclusive
      onChange={onChange}
      aria-label={ariaLabel}
      sx={styles}
    >
      {children}
    </ToggleButtonGroup>
  );
};

export default ToggleRouterTabSwitcher;
